import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { DEFAULT_SIGNUP_DOMAINS, PASSWORD_MIN, checkPassword, isAllowedSignupDomain } from "@brasilnovo/shared";
import { useAuth } from "../lib/auth.js";

type Modo = "entrar" | "cadastrar";

// Mensagem de erro da API, quando houver — o servidor explica melhor que
// "algo deu errado" (domínio não permitido, e-mail já cadastrado, etc.).
function mensagemDoErro(e: unknown, padrao: string): string {
  const resp = (e as { response?: { data?: { error?: string; details?: { fieldErrors?: Record<string, string[]> } } } })?.response?.data;
  const campo = resp?.details?.fieldErrors;
  if (campo) {
    const primeiro = Object.values(campo).flat().filter(Boolean)[0];
    if (primeiro) return String(primeiro);
  }
  return resp?.error ?? padrao;
}

export function Login() {
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const [modo, setModo] = useState<Modo>("entrar");
  const [email, setEmail] = useState("admin@brasilnovo.local");
  const [nome, setNome] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const dominios = DEFAULT_SIGNUP_DOMAINS.map((d) => `@${d}`).join(" ou ");
  const problemasSenha = modo === "cadastrar" && password ? checkPassword(password).problemas : [];
  // Aviso de domínio ANTES de enviar: o servidor recusa de qualquer forma,
  // mas descobrir isso só depois de preencher o formulário inteiro irrita.
  const dominioOk = !email || isAllowedSignupDomain(email);

  function trocarModo(novo: Modo) {
    setModo(novo);
    setError(null);
    setPassword("");
    if (novo === "cadastrar" && email.endsWith("@brasilnovo.local")) setEmail("");
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      if (modo === "entrar") {
        await login(email, password);
      } else {
        await register({ email, name: nome, password });
      }
      navigate("/", { replace: true });
    } catch (err) {
      setError(
        modo === "entrar"
          ? "E-mail ou senha inválidos."
          : mensagemDoErro(err, "Não foi possível concluir o cadastro."),
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-brand-600 via-brand-800 to-brand-950 px-4 py-8">
      <form onSubmit={submit} className="w-full max-w-sm overflow-hidden rounded-2xl border border-white/10 bg-white shadow-2xl">
        <div className="brand-stripe h-1.5 w-full" />
        <div className="p-8">
          <div className="mb-6 flex flex-col items-center text-center">
            <img
              src="/icons/caminhao-brasil-novo.png"
              alt="Caminhão com a marca Grupo Brasil Novo estampada na lateral"
              width={1254}
              height={1254}
              fetchPriority="high"
              className="mb-4 h-48 w-full object-cover sm:h-52"
            />
            <div className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-500">
              Controle Operacional
            </div>
          </div>

          {/* Alternador entre entrar e cadastrar.
              `role="tab"`, não `button`: o rótulo da aba ativa é o MESMO do
              botão de enviar ("Entrar" / "Criar conta"), e dois botões com o
              mesmo nome acessível deixam a tela ambígua — para leitor de tela
              e para qualquer automação que procure "o botão Entrar". */}
          <div role="tablist" aria-label="Entrar ou criar conta" className="mb-6 grid grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1">
            {(["entrar", "cadastrar"] as const).map((m) => (
              <button
                key={m}
                type="button"
                role="tab"
                aria-selected={modo === m}
                data-testid={`modo-${m}`}
                onClick={() => trocarModo(m)}
                className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${
                  modo === m ? "bg-white text-brand-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {m === "entrar" ? "Entrar" : "Criar conta"}
              </button>
            ))}
          </div>

          {modo === "cadastrar" && (
            <>
              <label className="label">Nome completo</label>
              <input
                className="input mb-4" data-testid="cadastro-nome"
                value={nome} onChange={(e) => setNome(e.target.value)} required minLength={3}
              />
            </>
          )}

          <label className="label">E-mail</label>
          <input
            className="input" type="email" data-testid="login-email"
            value={email} onChange={(e) => setEmail(e.target.value)} required
          />
          {modo === "cadastrar" && (
            <p className={`mb-4 mt-1 text-xs ${dominioOk ? "text-slate-400" : "text-amber-700"}`} data-testid="aviso-dominio">
              {dominioOk
                ? `Use seu e-mail corporativo ${dominios}.`
                : `O cadastro é restrito a e-mails ${dominios}.`}
            </p>
          )}
          {modo === "entrar" && <div className="mb-4" />}

          <label className="label">Senha</label>
          <input
            className="input" type="password" data-testid="login-senha"
            autoComplete={modo === "entrar" ? "current-password" : "new-password"}
            value={password} onChange={(e) => setPassword(e.target.value)} required
          />
          {modo === "cadastrar" && (
            <ul className="mb-4 mt-1 space-y-0.5 text-xs">
              {problemasSenha.length > 0 ? (
                problemasSenha.map((p) => <li key={p} className="text-amber-700">• {p}</li>)
              ) : (
                <li className="text-slate-400">
                  {password ? "Senha aceita." : `Ao menos ${PASSWORD_MIN} caracteres, combinando 3 tipos.`}
                </li>
              )}
            </ul>
          )}
          {modo === "entrar" && <div className="mb-4" />}

          {error && <div className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="login-erro">{error}</div>}

          <button
            className="btn-primary w-full"
            data-testid="login-enviar"
            disabled={loading || (modo === "cadastrar" && (!dominioOk || problemasSenha.length > 0))}
          >
            {loading
              ? modo === "entrar" ? "Entrando…" : "Criando conta…"
              : modo === "entrar" ? "Entrar" : "Criar conta"}
          </button>

          {modo === "entrar" ? (
            <p className="mt-4 text-center text-xs text-slate-400">Demo: admin@brasilnovo.local / brasilnovo123</p>
          ) : (
            <p className="mt-4 text-center text-xs text-slate-400">
              A conta nasce com acesso de <b>leitura</b>. Peça ao administrador
              a liberação do seu papel na operação.
            </p>
          )}
        </div>
      </form>
    </div>
  );
}
